import React from "react";
import Footer from "../layout/Footer";
import Hero from "./Hero";
import AboutSection from "./AboutSection";
import ContactSection from "./ContactSection";
import MiscSection from "./MiscSection";
import Testimonial from "./Testimonial";
import BenefitSection from "./BenefitSection";
import CompanyInfo from "./CompanyInfo";
import CandidateCTA from "./CandidateCTA";
import MapSection from "./MapSection";
import ScrollToTop from "../common/ScrollToTop";
import { useSections } from "../../hooks/useSections";

// Maps slug -> section renderer. Each receives the PageSection record (or null).
const SECTION_REGISTRY = {
  hero:            (sd) => <Hero sectionData={sd} />,
  about:           (sd) => <AboutSection sectionData={sd} />,
  "company-info":  (sd) => <CompanyInfo sectionData={sd} />,
  "our-services":  (sd) => <MiscSection sectionData={sd} />,
  benefits:        (sd) => <BenefitSection sectionData={sd} />,
  "candidate-cta": (sd) => <CandidateCTA sectionData={sd} />,
  testimonials:    (sd) => <Testimonial sectionData={sd} />,
  contact:         (sd) => <ContactSection sectionData={sd} />,
  map:             ()   => <MapSection />,
};

// Fallback render order when no DB sections exist yet (zero-downtime rollout)
const FALLBACK_ORDER = [
  "hero",
  "about",
  "company-info",
  "our-services",
  "benefits",
  "candidate-cta",
  "testimonials",
  "contact",
  "map",
];

const SectionSkeleton = ({ height = "h-64" }) => (
  <div className="animate-pulse">
    <div className={`py-16 bg-white ${height}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
        <div className="h-3 w-20 bg-slate-200 rounded-full" />
        <div className="h-8 w-1/3 bg-slate-200 rounded-xl" />
        <div className="h-4 w-2/3 bg-slate-100 rounded-full" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 bg-slate-100 rounded-2xl" />
          ))}
        </div>
      </div>
    </div>
  </div>
);

const LandingPage = () => {
  const { sections, loaded } = useSections("landing");

  const slugsToRender =
    sections.length > 0
      ? sections.filter((s) => s.isActive).map((s) => s.slug)
      : FALLBACK_ORDER;

  // Show skeleton placeholder while sections are loading
  if (!loaded) {
    return (
      <>
        <div className="min-h-screen bg-slate-900 animate-pulse" />
        <SectionSkeleton height="h-48" />
        <SectionSkeleton />
        <SectionSkeleton height="h-56" />
      </>
    );
  }

  return (
    <>
      {slugsToRender.map((slug) => {
        const renderer = SECTION_REGISTRY[slug];
        if (!renderer) return null;
        const sectionData = sections.find((s) => s.slug === slug) ?? null;
        return (
          <React.Fragment key={slug}>{renderer(sectionData)}</React.Fragment>
        );
      })}
      <Footer />
      <ScrollToTop />
    </>
  );
};

export default LandingPage;
